import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertIcon } from '../../design-system/alert';
import { useUserCountMetadata } from '../../queries/users';
import { GroupType } from '../../schemas/groups';
import { Conceal } from '../display/conceal';
import { Reveal } from '../display/reveal';

// Examples: { attr: 'role', value: 'account executive' }, { attr: 'department', value: 'sales' }, { attr: 'has_reports?', value: 'true' }
export type EligibilityCountCondition = { attr: string | undefined; value: string | undefined };

type EligibilityCountProps = {
	conditions: EligibilityCountCondition[];
	excludeConditions?: EligibilityCountCondition[];
	operator: GroupType;
	translationNamespace?: string;
	translationKeyPrefix?: string;
	/** Use a different key for the count message (e.g. "audienceCount_nominator" for nominator section). Default "audienceCount". */
	countMessageKey?: string;
};

export const EligibilityCount: FC<EligibilityCountProps> = ({
	conditions,
	excludeConditions = [],
	operator,
	translationNamespace = 'awards',
	translationKeyPrefix = 'form',
	countMessageKey = 'audienceCount',
}) => {
	const { t } = useTranslation(translationNamespace, { keyPrefix: translationKeyPrefix });

	const filteredConditions = useMemo(() => {
		return conditions.filter((condition) => condition.attr && condition.value) as { attr: string; value: string }[];
	}, [conditions]);

	const filteredExcludeConditions = useMemo(() => {
		return excludeConditions.filter((condition) => condition.attr && condition.value) as { attr: string; value: string }[];
	}, [excludeConditions]);

	const hasExcludes = filteredExcludeConditions.length > 0;
	const hasActiveFilters = filteredConditions.length > 0 || hasExcludes;
	const hasIncompleteCondition = conditions.some((c) => c.attr && !c.value);

	const { data } = useUserCountMetadata({
		conditions: filteredConditions,
		operator,
		status: 'active',
		...(hasExcludes ? { excludeConditions: filteredExcludeConditions } : {}),
	});

	if (hasIncompleteCondition) {
		return (
			<Alert status="warn" size="sm" mb={4}>
				<AlertIcon />
				<AlertDescription>{t(`${countMessageKey}_scoped_zero`)}</AlertDescription>
			</Alert>
		);
	}

	if (!hasActiveFilters || !data) {
		return null;
	}

	const displayCount = data.scopedCount;
	const countMessage =
		filteredConditions.length > 0 && displayCount === data.totalCount
			? t(`${countMessageKey}_matches_all`, { count: displayCount })
			: t(countMessageKey, { count: displayCount, context: 'scoped' });

	return (
		<>
			<Conceal if={displayCount === 0}>
				<Alert status="info" size="sm" mb={4}>
					<AlertIcon />
					<AlertDescription>{countMessage}</AlertDescription>
				</Alert>
			</Conceal>
			<Reveal if={displayCount === 0}>
				<Alert status="error" size="sm" mb={4}>
					<AlertIcon />
					<AlertDescription>{t(`${countMessageKey}_scoped_zero`)}</AlertDescription>
				</Alert>
			</Reveal>
		</>
	);
};
